import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  type CreateApiKeyRequest,
  type ApiKeyApprovalConfig as GeneratedAPIKeyApprovalConfig,
  type ApiKeyPermissions as GeneratedAPIKeyPermissions,
  type UpdateApiKeyApprovalConfigRequest,
  type UpdateApiKeyRequest,
  apikeyCreateApiKey,
  apikeyGetApiKeyOptions,
  apikeyGetApiKeyQueryKey,
  apikeyListApiKeysQueryKey,
  apikeyRevealApiKey,
  apikeyUpdateApiKeyMutation,
} from '@column/api';
import { Button, Dropdown, Icon, Input, Textarea } from '@column/column-ui-kit';
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from '@column/ui';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { HiddenInput, Loading, PopoverBase, RenderFields, RestrictedBanner } from '~/components';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import {
  type ApprovalAmounts,
  type ApprovalCondition,
  type ApprovalConditions,
  CONFIGURABLE_RULE_KEYS,
  type ConfigurableApprovalRuleKey,
  TransferApprovalConfiguration,
  approvalAmountsFromRules,
  approvalConditionsFromConfig,
  buildApprovalRules,
} from '~/lib/transfers';
import { type TransferApprovalRules } from '~/repositories/PlatformRepository';
import { isStepUpRequiredError } from '~/repositories/StepUpRepository';
import { useAPIKeyStore } from '~/stores/APIKey';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { StepUpCancelledError, useStepUpStore } from '~/stores/StepUp';
import { Inner } from '~/styles';
import { keysToCamelCase, keysToSnakeCase } from '~/util/convertKeys';

import { useDeleteAPIKeyAlert } from './_hooks/useDeleteAPIKeyAlert';
import {
  type APIKeyGranularPermissionLevel,
  APIKeyPermissionPreset,
  type APIKeyPermissions,
  apiKeyPermissionPresetOptions,
  apiKeyVisibleBankAccountLevelOverrideKeys,
  apiKeyVisibleEntityLevelOverrideKeys,
  createAPIKeyPlatformLevelPermissions,
  getAPIKeyPermissionPresetFromPermissions,
  normalizeAPIKeyPermissions,
} from './permissions';
import { setDefaultOverrides } from './utils';

interface Params {
  id: string;
}

enum Section {
  Transfers = 'api-key-transfers-and-accounts',
  Additional = 'api-key-additional-permissions',
}

enum APIKeyApprovalPreset {
  NotRequired = 'not-required',
  Required = 'required',
  Custom = 'custom',
}

interface APIKeyFormValues {
  apiKeyToken?: string;
  approvalAmounts: ApprovalAmounts;
  approvalConditions: ApprovalConditions;
  description: string;
  name: string;
  permissions: APIKeyPermissions;
  platformId: string;
}

type APIKeySaveValues = Omit<APIKeyFormValues, 'apiKeyToken' | 'approvalAmounts' | 'approvalConditions'> & {
  approvalConfig: UpdateApiKeyApprovalConfigRequest | null;
};

type UpdateAPIKeyRequestWithNullableClears = Omit<UpdateApiKeyRequest, 'approval_config' | 'permissions'> & {
  approval_config?: UpdateApiKeyApprovalConfigRequest | null;
  permissions?: GeneratedAPIKeyPermissions | null;
};

interface PermissionsPanelSnapshot {
  approvalAmounts: ApprovalAmounts;
  approvalConditions: ApprovalConditions;
  approvalPreset: APIKeyApprovalPreset;
  permissions: APIKeyPermissions;
  preset: APIKeyPermissionPreset;
}

interface ApprovalsPanelSnapshot {
  approvalAmounts: ApprovalAmounts;
  approvalConditions: ApprovalConditions;
  preset: APIKeyApprovalPreset;
}

const excludedAPIKeyPermissionKeys = ['apiKeys', 'approveTransfers'] as const;
const includedAPIKeyAdditionalPermissionKeys = ['counterparties', 'loans', 'reporting', 'webhooks'] as const;

const apiKeyApprovalPresetOptions = [
  {
    icon: <Icon.Close className="[--icon-color:var(--color-red-600)]!" />,
    label: 'Not Required',
    value: APIKeyApprovalPreset.NotRequired,
  },
  {
    icon: <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]!" />,
    label: 'Required',
    value: APIKeyApprovalPreset.Required,
  },
  {
    icon: <Icon.Filters className="[--icon-color:var(--color-yellow-600)]!" />,
    label: 'Custom',
    value: APIKeyApprovalPreset.Custom,
  },
];
const apiKeySections = [
  { label: 'Transfers and Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];
const MASKED_API_KEY = '•'.repeat(24);

const apiKeyApprovalTransferPermissionKeys = [
  'achCredits',
  'achDebits',
  'wireTransfers',
  'realtimeTransfers',
  'issueChecks',
] as const;

const createAPIKeyApprovalConditions = (condition: ApprovalCondition): ApprovalConditions =>
  Object.fromEntries(CONFIGURABLE_RULE_KEYS.map((key) => [key, condition]));

const createDefaultAPIKeyPermissions = (): APIKeyPermissions => ({
  bankAccountLevelOverrides: [],
  entityLevelOverrides: [],
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write'),
});

const createPresetAPIKeyPermissions = (permissionLevel: 'read' | 'write', platformId?: string): APIKeyFormValues => ({
  ...defaultFormValues(platformId),
  permissions: {
    bankAccountLevelOverrides: [],
    entityLevelOverrides: [],
    platformLevelPermissions: createAPIKeyPlatformLevelPermissions(permissionLevel),
  },
});

const defaultFormValues = (platformId?: string): APIKeyFormValues => ({
  apiKeyToken: '',
  approvalAmounts: {},
  approvalConditions: createAPIKeyApprovalConditions('disabled'),
  description: '',
  name: '',
  permissions: createDefaultAPIKeyPermissions(),
  platformId: platformId ?? '',
});

const apiKeyPermissionsToRequest = (permissions: APIKeyPermissions): GeneratedAPIKeyPermissions =>
  keysToSnakeCase(permissions) as GeneratedAPIKeyPermissions;

const apiKeyApprovalConfigToForm = (
  approvalConfig: GeneratedAPIKeyApprovalConfig
): Pick<APIKeyFormValues, 'approvalAmounts' | 'approvalConditions'> => {
  const rules = approvalConfig.rules ? (keysToCamelCase(approvalConfig.rules) as TransferApprovalRules) : null;
  return {
    approvalAmounts: approvalAmountsFromRules(rules),
    approvalConditions: approvalConditionsFromConfig(
      {
        achCredits: approvalConfig.enabled_for_ach_credits,
        achDebits: approvalConfig.enabled_for_ach_debits,
        checkDebits: approvalConfig.enabled_for_check_debits,
        internationalWire: approvalConfig.enabled_for_international_wire,
        realtime: approvalConfig.enabled_for_realtime,
        wire: approvalConfig.enabled_for_wire,
      },
      rules
    ),
  };
};

const apiKeyApprovalConfigToRequest = (
  approvalConditions: ApprovalConditions,
  approvalAmounts: ApprovalAmounts
): UpdateApiKeyApprovalConfigRequest => {
  const isEnabled = (key: ConfigurableApprovalRuleKey) => (approvalConditions[key] ?? 'disabled') !== 'disabled';
  const rules = buildApprovalRules({ amounts: approvalAmounts, conditions: approvalConditions });
  return {
    enabled_for_ach_credits: isEnabled('achCredits'),
    enabled_for_ach_debits: isEnabled('achDebits'),
    enabled_for_check_debits: isEnabled('checkDebits'),
    enabled_for_international_wire: isEnabled('internationalWire'),
    enabled_for_realtime: isEnabled('realtime'),
    enabled_for_wire: isEnabled('wire'),
    rules: rules ? (keysToSnakeCase(rules) as UpdateApiKeyApprovalConfigRequest['rules']) : null,
  };
};

const normalizeAPIKeyGranularPermissionLevelForRequest = (
  permissionLevel?: APIKeyGranularPermissionLevel
): APIKeyGranularPermissionLevel | undefined => (permissionLevel === 'create-delete' ? 'write' : permissionLevel);

const normalizeAPIKeyPermissionsForRequest = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: permissions.bankAccountLevelOverrides?.map((override) => ({
    ...override,
    thisBankAccount: normalizeAPIKeyGranularPermissionLevelForRequest(override.thisBankAccount),
  })),
  entityLevelOverrides: permissions.entityLevelOverrides?.map((override) => ({
    ...override,
    bankAccounts: normalizeAPIKeyGranularPermissionLevelForRequest(override.bankAccounts),
  })),
  platformLevelPermissions: {
    ...permissions.platformLevelPermissions,
    bankAccounts: normalizeAPIKeyGranularPermissionLevelForRequest(permissions.platformLevelPermissions.bankAccounts),
  },
});

const sanitizeAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: setDefaultOverrides(
    permissions.bankAccountLevelOverrides,
    'bankAccountId',
    apiKeyVisibleBankAccountLevelOverrideKeys
  ),
  entityLevelOverrides: setDefaultOverrides(
    permissions.entityLevelOverrides,
    'entityId',
    apiKeyVisibleEntityLevelOverrideKeys
  ),
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', permissions.platformLevelPermissions),
});

const getPermissionSavePayload = (
  preset: APIKeyPermissionPreset,
  permissions: APIKeyPermissions
): GeneratedAPIKeyPermissions | null =>
  preset === APIKeyPermissionPreset.FullAccess
    ? null
    : apiKeyPermissionsToRequest(normalizeAPIKeyPermissionsForRequest(sanitizeAPIKeyPermissions(permissions)));

const canAPIKeyCreateTransfers = (permissions?: APIKeyPermissions): boolean => {
  const hasWritePermission = (
    permissionSet?: Partial<Record<(typeof apiKeyApprovalTransferPermissionKeys)[number], unknown>>
  ) => apiKeyApprovalTransferPermissionKeys.some((permission) => permissionSet?.[permission] === 'write');

  return (
    hasWritePermission(permissions?.platformLevelPermissions) ||
    (permissions?.entityLevelOverrides ?? []).some(hasWritePermission) ||
    (permissions?.bankAccountLevelOverrides ?? []).some(hasWritePermission)
  );
};

const getAPIKeyApprovalPresetFromConditions = (approvalConditions: ApprovalConditions): APIKeyApprovalPreset => {
  const conditions = CONFIGURABLE_RULE_KEYS.map((key) => approvalConditions[key] ?? 'disabled');
  if (conditions.every((condition) => condition === 'all')) return APIKeyApprovalPreset.Required;
  if (conditions.every((condition) => condition === 'disabled')) return APIKeyApprovalPreset.NotRequired;
  return APIKeyApprovalPreset.Custom;
};

const getApprovalConfigSavePayload = (
  preset: APIKeyApprovalPreset,
  approvalConditions: ApprovalConditions,
  approvalAmounts: ApprovalAmounts
): UpdateApiKeyApprovalConfigRequest | null => {
  if (preset !== APIKeyApprovalPreset.NotRequired) {
    return apiKeyApprovalConfigToRequest(approvalConditions, approvalAmounts);
  }
  return null;
};

const copyAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: (permissions.bankAccountLevelOverrides ?? []).map((override) => ({ ...override })),
  entityLevelOverrides: (permissions.entityLevelOverrides ?? []).map((override) => ({ ...override })),
  platformLevelPermissions: { ...permissions.platformLevelPermissions },
});

const getAPIKeySaveValues = (data: APIKeyFormValues, approvalPreset: APIKeyApprovalPreset): APIKeySaveValues => ({
  approvalConfig: getApprovalConfigSavePayload(approvalPreset, data.approvalConditions, data.approvalAmounts),
  description: data.description,
  name: data.name,
  permissions: data.permissions,
  platformId: data.platformId,
});

const serializeAPIKeySaveValues = (values: APIKeySaveValues): string => JSON.stringify(values);

const getAPIErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorRecord = error as { code?: unknown };

  if (typeof errorRecord.code === 'string') {
    return errorRecord.code;
  }

  return undefined;
};

export const PageApiKeysEdit: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized, isSandbox } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const queryClient = useQueryClient();
  const [apiKeyName, setApiKeyName] = useState<string>('');
  const hasHandoffAPIKeyToken = useAPIKeyStore((state) => !!id && state.apiKeyId === id && !!state.token);
  const [firstViewAPIKey, setFirstViewAPIKey] = useState<{ id: string; token: string } | null>(null);
  const [revealedAPIKey, setRevealedAPIKey] = useState<{ id: string; token: string } | null>(null);
  const createdAPIKey = firstViewAPIKey && firstViewAPIKey.id === id ? firstViewAPIKey : null;
  const autoRevealed = !!createdAPIKey;
  const visibleAPIKeyToken =
    createdAPIKey?.token ?? (revealedAPIKey && revealedAPIKey.id === id ? revealedAPIKey.token : '');

  useEffect(() => {
    if (!id || !hasHandoffAPIKeyToken) {
      return;
    }
    const token = useAPIKeyStore.getState().consume(id);
    if (token) {
      setFirstViewAPIKey({ id, token });
    }
  }, [hasHandoffAPIKeyToken, id]);

  useEffect(() => {
    setFirstViewAPIKey((current) => (current && current.id !== id ? null : current));
  }, [id]);
  const [visibleSection, setVisibleSection] = useState<Section>(Section.Transfers);
  const [isPermissionsPanelOpen, setIsPermissionsPanelOpen] = useState<boolean>(false);
  const [isApprovalsPanelOpen, setIsApprovalsPanelOpen] = useState<boolean>(false);
  const [savedAPIKeySaveValues, setSavedAPIKeySaveValues] = useState<APIKeySaveValues | null>(null);
  const [selectedPermissionPreset, setSelectedPermissionPreset] = useState<APIKeyPermissionPreset>(
    APIKeyPermissionPreset.FullAccess
  );
  const [selectedApprovalPreset, setSelectedApprovalPreset] = useState<APIKeyApprovalPreset>(
    APIKeyApprovalPreset.NotRequired
  );

  const formMethods = useForm<APIKeyFormValues>({
    defaultValues: defaultFormValues(currentPlatform?.id ?? currentUser?.defaultPlatformId),
  });
  const { control, getValues, handleSubmit, reset, setValue, trigger } = formMethods;

  const permissionsPanelSnapshot = useRef<PermissionsPanelSnapshot | null>(null);
  const approvalsPanelSnapshot = useRef<ApprovalsPanelSnapshot | null>(null);

  const formValues = useWatch({ control }) as APIKeyFormValues;
  const bankAccountLevelOverrides = useWatch({ control, name: 'permissions.bankAccountLevelOverrides' });
  const entityLevelOverrides = useWatch({ control, name: 'permissions.entityLevelOverrides' });

  const selectedMode = isSandbox ? Mode.Sandbox : Mode.Live;
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId ?? '';
  const apiKeyQuery = useQuery({
    ...apikeyGetApiKeyOptions({
      path: { api_key_id: id ?? '', platform_id: platformId },
    }),
    enabled: !!id && !!platformId,
    retry: (failureCount, error) => getAPIErrorCode(error) !== 'invalid_resource_id_format' && failureCount < 3,
  });
  const handleRevealAPIKey = useCallback(async () => {
    if (!id) {
      return false;
    }
    try {
      if (useStepUpStore.getState().declined) {
        await useStepUpStore.getState().requireStepUp();
      }
      const { data } = await apikeyRevealApiKey({
        path: { api_key_id: id, platform_id: platformId },
        throwOnError: true,
      });
      setRevealedAPIKey({ id, token: data.api_key_token });
      return true;
    } catch (error) {
      if (!(error instanceof StepUpCancelledError) && !isStepUpRequiredError(error)) {
        addDangerNotification({ content: getErrorMessage(error) });
      }
      return false;
    }
  }, [addDangerNotification, id, platformId]);

  const createAPIKey = useCallback(
    (body: CreateApiKeyRequest) => apikeyCreateApiKey({ body, throwOnError: true }).then(({ data }) => data),
    []
  );
  const updateAPIKey = useMutation(apikeyUpdateApiKeyMutation());
  const openDeleteAPIKeyAlert = useDeleteAPIKeyAlert({
    platformId,
    redirectOnDelete: true,
  });

  const existingBankAccountIDs = useMemo(
    () =>
      (bankAccountLevelOverrides ?? [])
        .map((bankAccount) => bankAccount.bankAccountId)
        .filter((bankAccountId): bankAccountId is string => typeof bankAccountId === 'string'),
    [bankAccountLevelOverrides]
  );
  const existingEntityIDs = useMemo(
    () =>
      (entityLevelOverrides ?? [])
        .map((entity) => entity.entityId)
        .filter((entityId): entityId is string => typeof entityId === 'string'),
    [entityLevelOverrides]
  );

  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: true,
    entityIds: existingEntityIDs,
    mode: Mode.Sandbox,
  });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: true,
    entityIds: existingEntityIDs,
    mode: Mode.Live,
  });

  const openPermissionsPanel = useCallback(() => {
    permissionsPanelSnapshot.current = {
      approvalAmounts: { ...getValues('approvalAmounts') },
      approvalConditions: { ...getValues('approvalConditions') },
      approvalPreset: selectedApprovalPreset,
      permissions: copyAPIKeyPermissions({
        bankAccountLevelOverrides: getValues('permissions.bankAccountLevelOverrides') ?? [],
        entityLevelOverrides: getValues('permissions.entityLevelOverrides') ?? [],
        platformLevelPermissions:
          getValues('permissions.platformLevelPermissions') ??
          createDefaultAPIKeyPermissions().platformLevelPermissions,
      }),
      preset: selectedPermissionPreset,
    };
    setIsPermissionsPanelOpen(true);
  }, [getValues, selectedApprovalPreset, selectedPermissionPreset]);

  const closePermissionsPanel = useCallback(() => {
    setIsPermissionsPanelOpen(false);
    permissionsPanelSnapshot.current = null;
  }, []);

  const submitPermissionsPanel = useCallback(() => {
    setSelectedPermissionPreset(
      getAPIKeyPermissionPresetFromPermissions(sanitizeAPIKeyPermissions(getValues('permissions')))
    );
    closePermissionsPanel();
  }, [closePermissionsPanel, getValues]);

  const cancelPermissionsPanel = useCallback(() => {
    const snapshot = permissionsPanelSnapshot.current;

    if (snapshot) {
      setSelectedPermissionPreset(snapshot.preset);
      setSelectedApprovalPreset(snapshot.approvalPreset);
      setValue('approvalAmounts', snapshot.approvalAmounts, { shouldDirty: true });
      setValue('approvalConditions', snapshot.approvalConditions, { shouldDirty: true });
      setValue('permissions.bankAccountLevelOverrides', snapshot.permissions.bankAccountLevelOverrides, {
        shouldDirty: true,
      });
      setValue('permissions.entityLevelOverrides', snapshot.permissions.entityLevelOverrides, { shouldDirty: true });
      setValue('permissions.platformLevelPermissions', snapshot.permissions.platformLevelPermissions, {
        shouldDirty: true,
      });
    }

    closePermissionsPanel();
  }, [closePermissionsPanel, setValue]);

  const handlePermissionPresetChange = useCallback(
    (preset: APIKeyPermissionPreset) => {
      setSelectedPermissionPreset(preset);

      if (preset === APIKeyPermissionPreset.Custom) {
        setValue(
          'permissions.platformLevelPermissions',
          getValues('permissions.platformLevelPermissions') ??
            createDefaultAPIKeyPermissions().platformLevelPermissions,
          { shouldDirty: true, shouldTouch: true }
        );
        openPermissionsPanel();
        return;
      }

      const permissionLevel = preset === APIKeyPermissionPreset.ViewOnly ? 'read' : 'write';
      const presetPermissions = createPresetAPIKeyPermissions(permissionLevel, platformId);

      setIsPermissionsPanelOpen(false);
      setValue('permissions.bankAccountLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.entityLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.platformLevelPermissions', presetPermissions.permissions.platformLevelPermissions, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [getValues, openPermissionsPanel, platformId, setValue]
  );

  const openApprovalsPanel = useCallback(() => {
    approvalsPanelSnapshot.current = {
      approvalAmounts: { ...getValues('approvalAmounts') },
      approvalConditions: { ...getValues('approvalConditions') },
      preset: selectedApprovalPreset,
    };
    setIsApprovalsPanelOpen(true);
  }, [getValues, selectedApprovalPreset]);

  const closeApprovalsPanel = useCallback(() => {
    setIsApprovalsPanelOpen(false);
    approvalsPanelSnapshot.current = null;
  }, []);

  const submitApprovalsPanel = useCallback(async () => {
    if (!(await trigger('approvalAmounts'))) {
      return;
    }
    setSelectedApprovalPreset(getAPIKeyApprovalPresetFromConditions(getValues('approvalConditions')));
    closeApprovalsPanel();
  }, [closeApprovalsPanel, getValues, trigger]);

  const cancelApprovalsPanel = useCallback(() => {
    const snapshot = approvalsPanelSnapshot.current;

    if (snapshot) {
      setSelectedApprovalPreset(snapshot.preset);
      setValue('approvalAmounts', snapshot.approvalAmounts, { shouldDirty: true });
      setValue('approvalConditions', snapshot.approvalConditions, { shouldDirty: true });
    }

    closeApprovalsPanel();
  }, [closeApprovalsPanel, setValue]);

  const handleApprovalPresetChange = useCallback(
    (preset: APIKeyApprovalPreset) => {
      setSelectedApprovalPreset(preset);

      if (preset === APIKeyApprovalPreset.Custom) {
        setValue('approvalConditions', getValues('approvalConditions') ?? createAPIKeyApprovalConditions('disabled'), {
          shouldDirty: true,
          shouldTouch: true,
        });
        openApprovalsPanel();
        return;
      }

      setIsApprovalsPanelOpen(false);
      const nextApprovalConditions = createAPIKeyApprovalConditions(
        preset === APIKeyApprovalPreset.Required ? 'all' : 'disabled'
      );
      setValue('approvalConditions', nextApprovalConditions, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, openApprovalsPanel, setValue]
  );

  useBreadcrumbTitle(id ? apiKeyName : 'Create');

  useEffect(() => {
    if (!id) {
      setSelectedPermissionPreset(APIKeyPermissionPreset.FullAccess);
      setSelectedApprovalPreset(APIKeyApprovalPreset.NotRequired);
      setSavedAPIKeySaveValues(null);
      reset(defaultFormValues(platformId));
      return;
    }

    if (!apiKeyQuery.data) {
      return;
    }

    const response = apiKeyQuery.data;
    const responsePermissions = response.permissions
      ? normalizeAPIKeyPermissions(response.permissions)
      : createDefaultAPIKeyPermissions();
    const nextFormValues: APIKeyFormValues = {
      ...defaultFormValues(platformId),
      ...(response.approval_config ? apiKeyApprovalConfigToForm(response.approval_config) : {}),
      description: response.description,
      name: response.name,
      permissions: responsePermissions,
    };
    const nextPermissionPreset = response.permissions
      ? getAPIKeyPermissionPresetFromPermissions(responsePermissions)
      : APIKeyPermissionPreset.FullAccess;
    const nextApprovalPreset = response.approval_config
      ? getAPIKeyApprovalPresetFromConditions(nextFormValues.approvalConditions)
      : APIKeyApprovalPreset.NotRequired;

    setApiKeyName(response.name);
    setSelectedPermissionPreset(nextPermissionPreset);
    setSelectedApprovalPreset(nextApprovalPreset);
    setSavedAPIKeySaveValues(getAPIKeySaveValues(nextFormValues, nextApprovalPreset));
    reset(nextFormValues);
  }, [apiKeyQuery.data, id, platformId, reset]);

  useEffect(() => {
    if (!id || !apiKeyQuery.error) {
      return;
    }

    navigate(ROUTE.PLATFORM_API_KEYS);
    if (getAPIErrorCode(apiKeyQuery.error) === 'invalid_resource_id_format') {
      addDangerNotification({
        content: 'API key not found',
      });
      return;
    }

    addDangerNotification({
      content: getErrorMessage(apiKeyQuery.error),
    });
  }, [addDangerNotification, apiKeyQuery.error, id, navigate]);

  const hasUnsavedChanges = useMemo(() => {
    if (!id) {
      return true;
    }

    if (!savedAPIKeySaveValues) {
      return false;
    }

    return (
      serializeAPIKeySaveValues(getAPIKeySaveValues(formValues, selectedApprovalPreset)) !==
      serializeAPIKeySaveValues(savedAPIKeySaveValues)
    );
  }, [formValues, id, savedAPIKeySaveValues, selectedApprovalPreset]);

  const canWriteAPIKeys = isAuthorized({ permission: 'apiKeys', permissionLevel: 'write' });
  // View-only keys can't send transfers, so they have nothing to approve. A key with no backing role is
  // unrestricted, which the presets express as full access.
  const apiKeyCanCreateTransfers =
    selectedPermissionPreset === APIKeyPermissionPreset.ViewOnly
      ? false
      : selectedPermissionPreset === APIKeyPermissionPreset.FullAccess ||
        canAPIKeyCreateTransfers(formValues.permissions);

  const handleSave = (data: APIKeyFormValues) => {
    if (!currentUser) {
      return;
    }

    const saveValues = getAPIKeySaveValues(data, selectedApprovalPreset);
    const permissionSavePayload = getPermissionSavePayload(selectedPermissionPreset, saveValues.permissions);
    const permissions = permissionSavePayload;
    const approvalConfig = saveValues.approvalConfig;

    if (id) {
      const updateBody: UpdateAPIKeyRequestWithNullableClears = {
        description: saveValues.description,
        name: saveValues.name,
        ...(typeof permissions !== 'undefined' ? { permissions } : {}),
        ...(typeof approvalConfig !== 'undefined' ? { approval_config: approvalConfig } : {}),
      };

      updateAPIKey
        .mutateAsync({
          body: updateBody as UpdateApiKeyRequest,
          path: { api_key_id: id, platform_id: saveValues.platformId },
        })
        .then(() => {
          setApiKeyName(saveValues.name);
          setSavedAPIKeySaveValues(saveValues);
          reset(data);
          queryClient.invalidateQueries({
            queryKey: apikeyGetApiKeyQueryKey({
              path: { api_key_id: id, platform_id: saveValues.platformId },
            }),
          });
          queryClient.invalidateQueries({
            queryKey: apikeyListApiKeysQueryKey({
              path: { platform_id: saveValues.platformId },
            }),
          });
          addSuccessNotification({
            content: 'API key updated',
          });
        })
        .catch((error) => {
          addDangerNotification({
            content: getErrorMessage(error),
          });
        });
      return;
    }

    const createBody = {
      description: saveValues.description,
      name: saveValues.name,
      platform_id: saveValues.platformId,
    };
    const createRequestBody: CreateApiKeyRequest = {
      ...createBody,
      ...(permissions !== null && typeof permissions !== 'undefined' ? { permissions } : {}),
      ...(approvalConfig !== null && typeof approvalConfig !== 'undefined' ? { approval_config: approvalConfig } : {}),
    };

    createAPIKey(createRequestBody)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: apikeyListApiKeysQueryKey({
            path: { platform_id: saveValues.platformId },
          }),
        });
        if (response.api_key_token) {
          useAPIKeyStore.getState().stash(response.id, response.api_key_token);
        }
        queryClient.setQueryData(
          apikeyGetApiKeyQueryKey({ path: { api_key_id: response.id, platform_id: platformId } }),
          { ...response, api_key_token: '' }
        );
        navigate(`${ROUTE.PLATFORM_API_KEYS}/edit/${response.id}`);
        addSuccessNotification({
          content: 'API key created',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: getErrorMessage(error),
        });
      });
  };
  const handleCancel = useCallback(() => {
    if (id) {
      navigate(ROUTE.PLATFORM_API_KEYS);
      return;
    }
    navigate(-1);
  }, [id, navigate]);
  const isEditLoading = !!id && apiKeyQuery.isLoading;

  const deleteAction = id ? (
    <Button
      isDisabled={!canWriteAPIKeys}
      onClick={() => openDeleteAPIKeyAlert({ id, name: apiKeyName })}
      size="small"
      variant="secondary-danger"
    >
      Delete API Key
    </Button>
  ) : null;
  const backAction = (
    <Button onClick={handleCancel} variant="secondary" size="small">
      Cancel
    </Button>
  );
  const saveAction = (
    <Button onClick={handleSubmit(handleSave)} isDisabled={!canWriteAPIKeys} size="small" icon={!id && <Icon.Plus />}>
      {id ? 'Save API Key' : 'Create API Key'}
    </Button>
  );
  const headerDecoration = currentPlatform ? (
    <Badge
      variant="outline"
      className={cn(
        'ml-1 translate-y-px rounded-md',
        isSandbox
          ? 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300'
          : 'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300'
      )}
    >
      {isSandbox ? 'Sandbox' : 'Live'}
    </Badge>
  ) : null;

  const actions =
    id && !hasUnsavedChanges ? (
      <PageActions key="api-key-back-action" headerDecoration={headerDecoration}>
        {deleteAction}
        {backAction}
      </PageActions>
    ) : (
      <PageActions key="api-key-save-actions" headerDecoration={headerDecoration}>
        {deleteAction}
        {backAction}
        {saveAction}
      </PageActions>
    );

  return (
    <>
      {actions}
      <Loading isLoading={isEditLoading}>
        {!canWriteAPIKeys && (
          <Inner pb={0}>
            <RestrictedBanner />
          </Inner>
        )}
        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={handleSubmit(handleSave)}>
            {RenderFields<APIKeyFormValues>({
              sections: [
                {
                  fields: [
                    {
                      id: 'name',
                      label: 'Name',
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Input
                            value={String(value)}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Name"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Textarea
                            value={value !== undefined ? String(value) : ''}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Description"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    {
                      id: 'permissions.platformLevelPermissions' as const,
                      label: 'Permissions',
                      children: () => (
                        <>
                          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                            <Dropdown
                              active={selectedPermissionPreset}
                              options={apiKeyPermissionPresetOptions}
                              onChange={(preset) => handlePermissionPresetChange(preset as APIKeyPermissionPreset)}
                              fullWidth
                            />
                            {selectedPermissionPreset === APIKeyPermissionPreset.Custom && (
                              <Button
                                className="h-10"
                                type="button"
                                variant="secondary"
                                size="small"
                                icon={<Icon.Edit />}
                                onClick={openPermissionsPanel}
                              >
                                Edit
                              </Button>
                            )}
                          </div>
                        </>
                      ),
                    },
                    {
                      id: 'approvalConditions.achCredits' as const,
                      label: 'Transfer Approvals',
                      description: 'Configure whether transfers sent by this key require approval.',
                      hide: !apiKeyCanCreateTransfers,
                      children: () => (
                        <div className="grid gap-1.5">
                          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                            <Dropdown
                              active={selectedApprovalPreset}
                              options={apiKeyApprovalPresetOptions}
                              onChange={(preset) => handleApprovalPresetChange(preset as APIKeyApprovalPreset)}
                              fullWidth
                            />
                            {selectedApprovalPreset === APIKeyApprovalPreset.Custom && (
                              <Button
                                className="h-10"
                                type="button"
                                variant="secondary"
                                size="small"
                                icon={<Icon.Edit />}
                                onClick={openApprovalsPanel}
                              >
                                Edit
                              </Button>
                            )}
                          </div>
                        </div>
                      ),
                    },
                  ],
                },
                {
                  fields: [
                    {
                      id: 'apiKeyToken',
                      label: 'API Key',
                      children: () => (
                        <HiddenInput
                          key={`${id ?? 'create'}-${autoRevealed ? 'auto-revealed' : 'masked'}`}
                          defaultIsVisible={autoRevealed}
                          value={visibleAPIKeyToken}
                          placeholder={id ? MASKED_API_KEY : 'Key Will be Generated'}
                          onReveal={id ? handleRevealAPIKey : undefined}
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
            <PopoverBase
              show={isPermissionsPanelOpen}
              usePortal
              withoutBackdrop
              className={cn(
                '![--animation-enter-x:24px] ![--animation-exit-x:24px] ![--animation-enter-y:0px] ![--animation-exit-y:0px]',
                '[--popover-left:-24px] [--popover-padding:0] [--popover-position:fixed] [--popover-right:0] [--popover-top:0px] [--popover-width:min(100vw,700px)]',
                'fixed! top-0! right-0! bottom-0! left-[-24px]! z-[14]! flex! h-dvh! w-[calc(100vw+24px)]! justify-end! overflow-hidden! bg-[rgba(18,18,22,0.56)]',
                '[&>div]:h-dvh! [&>div]:w-[min(100vw,700px)]! [&>div]:rounded-none! [&>div]:flex [&>div]:flex-col',
                '[&>div]:shadow-[1px_0_0_var(--color-border),-1px_0_0_var(--color-border),0_4px_16px_rgba(38,38,44,0.03)]!'
              )}
            >
              <div className="flex items-center gap-4 border-b border-gray-200 bg-background px-5 py-4">
                <h4 className="m-0 flex-auto text-base font-medium leading-5 text-foreground">Permissions</h4>
                <Button
                  type="button"
                  variant="subtle"
                  size="small"
                  icon={<Icon.Cross />}
                  onClick={cancelPermissionsPanel}
                />
              </div>
              <Tabs
                value={visibleSection}
                onValueChange={(section) => setVisibleSection(section as Section)}
                className="px-3 py-0.5 shadow-[0_1px_0_0_color-mix(in_oklch,var(--color-gray-950)_8%,transparent)] bg-gray-25 z-10"
              >
                <TabsList variant="ghost" className="gap-0.5">
                  {apiKeySections.map((section) => (
                    <TabsTrigger key={section.value} value={section.value}>
                      {section.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <div className="flex-auto overflow-y-auto [--page-margin:24px]">
                <PermissionConfiguration
                  visibleSection={
                    visibleSection === Section.Transfers
                      ? PermissionConfigurationSection.Transfers
                      : PermissionConfigurationSection.Additional
                  }
                  excludeKeys={[...excludedAPIKeyPermissionKeys]}
                  fieldPathPrefix="permissions"
                  forcedMode={selectedMode}
                  includeAdditionalPermissionKeys={[...includedAPIKeyAdditionalPermissionKeys]}
                  onOffEnabledPermissionLevel="read"
                  sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
                  liveEntitiesAndAccounts={liveEntitiesAndAccounts}
                />
              </div>
              <div className="box-border flex justify-between gap-5 border-t border-gray-200 bg-gray-25 px-6 py-5">
                <Button type="button" variant="secondary" onClick={cancelPermissionsPanel}>
                  Cancel
                </Button>
                <Button type="button" onClick={submitPermissionsPanel}>
                  Done
                </Button>
              </div>
            </PopoverBase>
            <Dialog
              open={isApprovalsPanelOpen}
              onOpenChange={(isOpen) => {
                if (!isOpen) {
                  cancelApprovalsPanel();
                }
              }}
            >
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Transfer Approvals</DialogTitle>
                  <DialogDescription>Select when transfers sent by this key will require approval.</DialogDescription>
                </DialogHeader>
                <div className="mb-2">
                  <TransferApprovalConfiguration canConfigure={canWriteAPIKeys} />
                </div>
                <DialogFooter className="sm:justify-between">
                  <Button type="button" variant="secondary" onClick={cancelApprovalsPanel}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={submitApprovalsPanel}>
                    Done
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </form>
        </FormProvider>
      </Loading>
    </>
  );
};
